// theming/AllThemesGallery.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';
import clsx from 'clsx';
import Heading from '@/dashboard/components/Formik/components/Heading';
import { Input } from '@dashboard/components/ui/input';
import { Button } from '@dashboard/components/ui/button';
import { ThemeCard } from './ThemeCard';
import { orderThemesForCards } from './orderThemesForCards';
import { getThemeCardVisuals } from './getThemeCardVisuals';

export function AllThemesGallery({
  themes,
  currentThemeId,
  defaultThemeId,
  colorMode,
  onSelect,
  onSetDefault,
  onDuplicate,
  onDelete,
  hideActions = false,
  onClose,
}) {
  const [query, setQuery] = useState('');
  const [isStuck, setIsStuck] = useState(false);
  const sentinelRef = useRef(null);

  useEffect(() => {
    const handler = e => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return undefined;
    const bannerHeight =
      parseInt(document.documentElement.style.getPropertyValue('--top-banner-h'), 10) || 0;
    const observer = new IntersectionObserver(([entry]) => setIsStuck(!entry.isIntersecting), {
      rootMargin: `-${56 + bannerHeight}px 0px 0px 0px`,
      threshold: 0,
    });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  const ordered = useMemo(
    () => orderThemesForCards(themes, { defaultThemeId }),
    [themes, defaultThemeId],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return ordered;
    return ordered.filter(theme => {
      const label = getThemeCardVisuals(theme).fontLabel.toLowerCase();
      return theme.name.toLowerCase().includes(q) || label.includes(q);
    });
  }, [ordered, query]);

  return (
    <div className="flex min-h-[calc(100vh-3.5rem)] flex-col bg-white dark:bg-background">
      <div ref={sentinelRef} aria-hidden="true" className="h-px w-full" />
      {/* Sticky header — picks up a bottom border once it pins to the viewport */}
      <div
        className={clsx(
          'sticky top-[calc(3.5rem_+_var(--top-banner-h,0px))] z-20 flex items-center gap-3 bg-white dark:bg-background px-4 pt-3 pb-4 transition-colors',
          isStuck ? 'border-b border-gray-200 dark:border-border' : 'border-b border-transparent',
        )}
      >
        <div className="flex-1">
          <Heading label="All themes" level={3} />
          <div className="text-xs text-gray-500 dark:text-muted-foreground mt-0.5">
            {themes?.length || 0} themes
          </div>
        </div>
        <div className="relative w-48">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 dark:text-muted-foreground" />
          <Input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search themes…"
            className="pl-8"
          />
        </div>
        <Button type="button" variant="ghost" size="icon" aria-label="Close" onClick={onClose}>
          <X />
        </Button>
      </div>

      {/* Grid */}
      <div className="flex-1 px-4 pb-4">
        {filtered.length === 0 ? (
          <div className="py-16 text-center text-sm text-gray-500 dark:text-muted-foreground">
            No themes match "{query}".
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-2.5">
            {filtered.map(theme => (
              <ThemeCard
                key={theme._id}
                theme={theme}
                active={theme._id === currentThemeId}
                isDefault={theme._id === defaultThemeId}
                canDelete={theme.scope === 'seller'}
                colorMode={colorMode}
                onSelect={onSelect}
                onSetDefault={onSetDefault}
                onDuplicate={onDuplicate}
                onDelete={onDelete}
            hideActions={hideActions}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default AllThemesGallery;
